/**
 * useCategoryGroupHandlers Hook
 * 
 * Handles category group CRUD operations (create, edit, delete, details management).
 */

import { TransactionCategoryGroup } from "../../../types";
import { api } from "../../../services/api";
import { isDemoMode } from "../../../utils/demoMode";

export interface UseCategoryGroupHandlersParams {
  workspaceId: string;
  selectedTab: "expense" | "income";
  groups: TransactionCategoryGroup[];
  categories: any[];
  setGroups: (groups: TransactionCategoryGroup[]) => void;
  setCategories: (categories: any[]) => void;
  // UI state setters
  setError: (error: string) => void;
  setSaving: (saving: boolean) => void;
  setCreatingGroup: (creating: boolean) => void;
  setEditGroupDialogOpen: (open: boolean) => void;
  setGroupDeleteDialogOpen: (open: boolean) => void;
  setGroupDetailsDialogOpen: (open: boolean) => void;
  setEditingGroup: (group: TransactionCategoryGroup | null) => void;
  setGroupToDelete: (group: TransactionCategoryGroup | null) => void;
  setEditGroupName: (name: string) => void;
  setEditingGroupDetails: (details: any[]) => void;
  setOriginalGroupDetails: (details: any[]) => void;
  setEditingGroupNameForDetails: (name: string) => void;
  setNewDetailName: (name: string) => void;
  setNewDetailType: (type: "list" | "measurable" | "") => void;
  setNewDetailUnit: (unit: string) => void;
  setNewListItems: (items: string[]) => void;
  setDetailNameError: (error: string) => void;
  setEditingDetailIndex: (index: number | null) => void;
  setEditingOriginalDetail: (detail: any | null) => void;
  // Helper functions
  isGroupUsed: (groupId: string) => boolean;
  countCategoriesInGroup: (groupId: string) => number;
  // Current state values
  creatingGroup: boolean;
  saving: boolean;
  editingGroup: TransactionCategoryGroup | null;
  editGroupName: string;
  groupToDelete: TransactionCategoryGroup | null;
}

export interface UseCategoryGroupHandlersReturn {
  handleCreateGroup: (groupName: string) => Promise<void>;
  handleEditGroup: (group: TransactionCategoryGroup) => void;
  handleSaveGroupEdit: () => Promise<void>;
  handleDeleteGroup: (group: TransactionCategoryGroup) => void;
  handleConfirmDeleteGroup: () => Promise<void>;
  handleGroupDetails: (group: TransactionCategoryGroup) => void;
}

/**
 * Hook for category group CRUD handlers
 */
export function useCategoryGroupHandlers({
  workspaceId,
  selectedTab,
  groups,
  categories,
  setGroups,
  setCategories,
  setError,
  setSaving,
  setCreatingGroup,
  setEditGroupDialogOpen,
  setGroupDeleteDialogOpen,
  setGroupDetailsDialogOpen,
  setEditingGroup,
  setGroupToDelete,
  setEditGroupName,
  setEditingGroupDetails,
  setOriginalGroupDetails,
  setEditingGroupNameForDetails,
  setNewDetailName,
  setNewDetailType,
  setNewDetailUnit,
  setNewListItems,
  setDetailNameError,
  setEditingDetailIndex,
  setEditingOriginalDetail,
  isGroupUsed,
  countCategoriesInGroup,
  creatingGroup,
  saving,
  editingGroup,
  editGroupName,
  groupToDelete,
}: UseCategoryGroupHandlersParams): UseCategoryGroupHandlersReturn {
  const handleCreateGroup = async (groupName: string): Promise<void> => {
    if (!groupName.trim() || creatingGroup || saving) return;

    try {
      setCreatingGroup(true);
      setError("");
      
      // Create the group
      const newGroup = await api.createGroup(workspaceId, groupName.trim(), selectedTab);
      
      // Refresh groups
      const groupsData = await api.getGroups(workspaceId, selectedTab);
      setGroups(groupsData);
      
      // Don't auto-select - let caller decide what to do
      setError("");
    } catch (err: any) {
      setError(err.message || "Failed to create group");
    } finally {
      setCreatingGroup(false);
    }
  };

  const handleEditGroup = (group: TransactionCategoryGroup): void => {
    setEditingGroup(group);
    setEditGroupName(group.name);
    setEditGroupDialogOpen(true);
  };

  const handleSaveGroupEdit = async (): Promise<void> => {
    if (!editingGroup || !editGroupName.trim()) return;

    setSaving(true);
    setError("");

    try {
      await api.updateGroup(workspaceId, editingGroup.id, editGroupName.trim());
      setEditGroupDialogOpen(false);
      setEditingGroup(null);
      setEditGroupName("");
      // Refresh groups
      const groupsData = await api.getGroups(workspaceId, selectedTab);
      setGroups(groupsData);
    } catch (err: any) {
      setError(err.message || "Failed to update group");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteGroup = (group: TransactionCategoryGroup): void => {
    if (isGroupUsed(group.id)) {
      alert(`Cannot delete: This group is used by ${countCategoriesInGroup(group.id)} categor${countCategoriesInGroup(group.id) === 1 ? "y" : "ies"}`);
      return;
    }
    setGroupToDelete(group);
    setGroupDeleteDialogOpen(true);
  };

  const handleConfirmDeleteGroup = async (): Promise<void> => {
    if (!groupToDelete) return;

    setSaving(true);
    setError("");

    try {
      await api.deleteGroup(workspaceId, groupToDelete.id);
      setGroupDeleteDialogOpen(false);
      setGroupToDelete(null);
      // Refresh groups
      const groupsData = await api.getGroups(workspaceId, selectedTab);
      setGroups(groupsData);
      // Also refresh categories in case any were affected
      const data = await api.getCategories(workspaceId, selectedTab);
      setCategories(data);
    } catch (err: any) {
      setError(err.message || "Failed to delete group");
    } finally {
      setSaving(false);
    }
  };

  const handleGroupDetails = (group: TransactionCategoryGroup): void => {
    const groupName = group.name;
    const originalDetails = [...(group.details || [])];
    setEditingGroupDetails(originalDetails);
    setOriginalGroupDetails(originalDetails);
    setEditingGroupNameForDetails(groupName);
    setNewDetailName("");
    setNewDetailType("");
    setNewDetailUnit("");
    setNewListItems([]);
    setDetailNameError("");
    setEditingDetailIndex(null);
    setEditingOriginalDetail(null);
    setGroupDetailsDialogOpen(true);
  };

  return {
    handleCreateGroup,
    handleEditGroup,
    handleSaveGroupEdit,
    handleDeleteGroup,
    handleConfirmDeleteGroup,
    handleGroupDetails,
  };
}
